import React, { useState } from "react";
import { Form, Input, Button, Select } from "antd";

import PlaceSearch from "../../Login/component/PlaceSearch";
import { REQUIRED_ADDRESS, addressLines, billingAddressFromPlace } from "./billingAddress";
import "./checkout.css";

const { Option } = Select;

/**
 * The billing details we must hold before an office can be charged.
 *
 * These go to `POST /payment/api/create-platform-customer/`, which creates the
 * Helcim customer the card is later attached to. Asking for exactly what that
 * customer record uses and nothing else:
 *
 *   contactName  <- first + last      billingAddress.street1   <- address1
 *   email                             billingAddress.city      <- city
 *   cellPhone    <- phone             billingAddress.province  <- state
 *                                     billingAddress.country / postalCode
 *
 * Date of birth used to be on this form. The API does not require it and the
 * provider is never sent it — `dateOfBirth` reaches a provider only on the
 * Dwolla branch — so it was collected from every customer and discarded. Gone.
 * (`ssn` in the same serializer is likewise Dwolla-only.)
 *
 * THE ADDRESS IS ONE QUESTION, NOT FIVE
 * -------------------------------------
 * Country, street, city, province and postal code were five things to type in a
 * form that already asked for four others. They are now one address search —
 * the same Google Places box the onboarding "basic information" step uses — and
 * the answer is shown back as an address rather than as five filled inputs.
 * "Change" opens them, and they open by themselves whenever Google's answer is
 * short of something we have to send (a business listing with no street line, a
 * rural result with no postal code), so a half-parsed address can never be
 * submitted silently.
 *
 * The fields stay mounted while collapsed so antd still validates them; a failed
 * submit expands them so the error is somewhere the customer can see it.
 *
 * ⚠️ `country` is a TWO-CHARACTER column on the provider's billing row and the
 * existing rows hold "CA" / "ON" — so the codes are written, not the names.
 *
 * Fields only: the caller owns what submitting means and what happens next.
 * `client_name: {}` is included because the package does an undefended
 * `data.pop('client_name')`.
 *
 * @param {Function} onSubmit  async (values) => void — ready to POST
 * @param {boolean}  submitting
 * @param {string}   [submitText]
 */

export default function PaymentProfileForm({ onSubmit, submitting, submitText = "Continue to payment" }) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  // The address as picked, for the summary block. Null until one is chosen.
  const [picked, setPicked] = useState(null);
  // Fields open. True until an address is picked, then only on request.
  const [editingAddress, setEditingAddress] = useState(true);
  // Set when a picked place left one of the required parts blank, so the hint
  // can say what happened instead of repeating the instruction.
  const [addressIncomplete, setAddressIncomplete] = useState(false);
  const loading = submitting !== undefined ? submitting : busy;

  const handleFinish = async (values) => {
    const payload = { ...values, client_name: {} };
    if (submitting === undefined) setBusy(true);
    try {
      await onSubmit(payload);
    } finally {
      if (submitting === undefined) setBusy(false);
    }
  };

  // A validation message attached to a collapsed field is a message nobody can
  // read — open them. Only for an address problem, though: a missing first name
  // is no reason to undo the tidying.
  const handleFinishFailed = ({ errorFields }) => {
    const failed = (errorFields || []).some((f) =>
      (f.name || []).some((n) => REQUIRED_ADDRESS.includes(n)));
    if (failed) setEditingAddress(true);
  };

  const handleAddress = (parsed) => {
    const { fields, complete } = billingAddressFromPlace(parsed);
    form.setFieldsValue(fields);
    setPicked(fields);
    // Anything Google left blank is still something the customer has to give us,
    // so the fields open rather than hiding a gap behind a tidy summary.
    setAddressIncomplete(!complete);
    setEditingAddress(!complete);
  };

  const lines = picked ? addressLines(picked) : null;

  return (
    <Form
      form={form}
      onFinish={handleFinish}
      onFinishFailed={handleFinishFailed}
      layout="vertical"
      className="co"
      requiredMark={false}
      scrollToFirstError
    >
      <div className="co-row">
        <Form.Item name="first_name" label="First name"
                   rules={[{ required: true, message: "Enter a first name" }]}>
          <Input placeholder="Ada" autoComplete="given-name" />
        </Form.Item>
        <Form.Item name="last_name" label="Last name"
                   rules={[{ required: true, message: "Enter a last name" }]}>
          <Input placeholder="Lovelace" autoComplete="family-name" />
        </Form.Item>
      </div>

      <div className="co-row">
        <Form.Item name="email" label="Email for receipts"
                   rules={[{ required: true, message: "Enter an email" },
                           { type: "email", message: "That doesn't look like an email" }]}>
          <Input placeholder="billing@yourpractice.com" autoComplete="email" />
        </Form.Item>
        {/* The provider's column is 10 characters — a longer value comes back as
            a field error the customer would never see. */}
        <Form.Item name="phone" label="Phone"
                   rules={[{ required: true, message: "Enter a phone number" }]}>
          <Input placeholder="4165550123" maxLength={10} autoComplete="tel-national" />
        </Form.Item>
      </div>

      <div className="co-section">Billing address</div>

      <div className="co-places">
        <PlaceSearch onAddress={handleAddress} placeholder="Start typing your billing address…" />
      </div>

      {lines && !editingAddress ? (
        <div className="co-address">
          <div className="co-address-lines">
            {lines.map((line, i) => <div key={i}>{line}</div>)}
          </div>
          <button type="button" className="co-linkbtn" onClick={() => setEditingAddress(true)}>
            Change
          </button>
        </div>
      ) : (
        <div className="co-hint">
          {addressIncomplete
            ? "That address is missing a few parts. Fill in what's blank below."
            : "Pick your address from the list and we'll fill in the rest, or type it in below."}
        </div>
      )}

      {/* Kept mounted while collapsed so the rules still run on submit. */}
      <div hidden={Boolean(lines) && !editingAddress}>
        <Form.Item name="address1" label="Street address"
                   rules={[{ required: true, message: "Enter a street address" }]}>
          <Input placeholder="120 Queen Street West" autoComplete="address-line1" />
        </Form.Item>

        <div className="co-row">
          <Form.Item name="city" label="City"
                     rules={[{ required: true, message: "Enter a city" }]}>
            <Input placeholder="Toronto" autoComplete="address-level2" />
          </Form.Item>
          <Form.Item name="state" label="Province or state"
                     rules={[{ required: true, message: "Enter a province or state" }]}>
            <Input placeholder="ON" autoComplete="address-level1" />
          </Form.Item>
        </div>

        <div className="co-row">
          <Form.Item name="postalCode" label="Postal code"
                     rules={[{ required: true, message: "Enter a postal code" }]}>
            <Input placeholder="M5H 2M9" autoComplete="postal-code" />
          </Form.Item>
          <Form.Item name="country" label="Country"
                     rules={[{ required: true, message: "Choose a country" }]}>
            <Select placeholder="Select country">
              <Option value="CA">Canada</Option>
              <Option value="US">United States</Option>
            </Select>
          </Form.Item>
        </div>
      </div>

      <Button block type="primary" htmlType="submit" loading={loading} className="co-submit">
        {submitText}
      </Button>
    </Form>
  );
}
